import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LayoutGrid } from 'lucide-react';
import clsx from 'clsx';
import type { GuestSource, GuestStatus, Reservation, RestaurantTable, TableStatus } from '@sebastian/shared';
import { api, ApiError } from '../lib/api';
import { useTables } from '../hooks/queries';
import { useNow } from '../hooks/useNow';
import { useToast } from '../hooks/useToast';
import { useCurrency } from '../lib/currency';
import { TableCard } from '../components/TableCard';
import { OpenTableDialog } from '../components/OpenTableDialog';
import { EmptyState, ErrorState, Spinner, Stat } from '../components/ui';

const FILTERS: { key: 'ALL' | TableStatus; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'AVAILABLE', label: 'Available' },
  { key: 'OCCUPIED', label: 'Occupied' },
  { key: 'WAITING_PAYMENT', label: 'Waiting Payment' },
  { key: 'RESERVED', label: 'Reserved' },
];

/** The floor plan: the first screen every shift starts on. */
export function PosPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const now = useNow(30_000);
  const { money } = useCurrency();

  const { data: tables, isLoading, error, refetch } = useTables();
  /** Bookings arriving in the next few hours, shown on the table they hold. */
  const { data: upcoming } = useQuery({
    queryKey: ['reservations', 'upcoming'],
    queryFn: api.reservations.upcoming,
    refetchInterval: 60_000,
  });
  const bookingByTable = new Map<string, Reservation>();
  for (const booking of upcoming ?? []) {
    if (booking.tableId && !bookingByTable.has(booking.tableId)) bookingByTable.set(booking.tableId, booking);
  }
  const [filter, setFilter] = useState<'ALL' | TableStatus>('ALL');
  const [pending, setPending] = useState<RestaurantTable | null>(null);

  const openTable = useMutation({
    mutationFn: ({
      tableId,
      guestCount,
      notes,
      guest,
    }: {
      tableId: string;
      guestCount: number;
      notes: string | null;
      guest: { guestSource: GuestSource | null; guestStatus: GuestStatus | null };
    }) => api.orders.open(tableId, guestCount, notes, guest),
    onSuccess: (order) => {
      void queryClient.invalidateQueries({ queryKey: ['tables'] });
      setPending(null);
      navigate(`/order/${order.tableId}`);
    },
    onError: (caught: unknown) => {
      toast.error(caught instanceof ApiError ? caught.message : 'Could not open that table.');
      void queryClient.invalidateQueries({ queryKey: ['tables'] });
    },
  });

  const sections = useMemo(() => {
    if (!tables) return [];
    const visible = filter === 'ALL' ? tables : tables.filter((table) => table.status === filter);
    const grouped = new Map<string, RestaurantTable[]>();
    for (const table of visible) {
      const key = table.section ?? 'Dining Room';
      grouped.set(key, [...(grouped.get(key) ?? []), table]);
    }
    return [...grouped.entries()];
  }, [tables, filter]);

  const stats = useMemo(() => {
    const list = tables ?? [];
    const active = list.filter((table) => table.activeOrder);
    return {
      free: list.filter((table) => table.status === 'AVAILABLE').length,
      open: active.length,
      guests: active.reduce((sum, table) => sum + (table.activeOrder?.guestCount ?? 0), 0),
      onFloor: active.reduce((sum, table) => sum + (table.activeOrder?.grandTotalCents ?? 0), 0),
    };
  }, [tables]);

  function onSelect(table: RestaurantTable) {
    if (table.activeOrder) {
      navigate(`/order/${table.id}`);
      return;
    }
    setPending(table);
  }

  return (
    <div className="mx-auto max-w-[1600px] px-5 py-6">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-6">
        <div>
          <h1 className="font-display text-3xl tracking-wide text-ink">Dining Room</h1>
          <p className="mt-1 text-sm text-ink-faint">
            Tap a free table to seat guests, or an open table to continue its order.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-8">
          <Stat label="Free" value={stats.free} />
          <Stat label="Open" value={stats.open} />
          <Stat label="Guests" value={stats.guests} />
          <Stat label="On the floor" value={money(stats.onFloor)} tone="accent" />
        </div>
      </header>

      <div className="mb-6 flex flex-wrap gap-2">
        {FILTERS.map((entry) => {
          const count =
            entry.key === 'ALL'
              ? (tables?.length ?? 0)
              : (tables?.filter((table) => table.status === entry.key).length ?? 0);
          return (
            <button
              key={entry.key}
              type="button"
              onClick={() => setFilter(entry.key)}
              className={clsx(
                'h-10 rounded-full border px-4 text-sm font-medium transition',
                filter === entry.key
                  ? 'border-accent bg-accent/12 text-accent-strong'
                  : 'border-line text-ink-soft hover:border-line-strong hover:text-ink',
              )}
            >
              {entry.label}
              <span className="tnum ml-2 text-xs text-ink-faint">{count}</span>
            </button>
          );
        })}
      </div>

      {isLoading ? <Spinner label="Loading floor plan…" /> : null}
      {error ? <ErrorState error={error} onRetry={() => void refetch()} /> : null}

      {!isLoading && !error && sections.length === 0 ? (
        <EmptyState
          icon={<LayoutGrid size={32} />}
          title="No tables to show"
          message="Nothing matches this filter right now."
        />
      ) : null}

      <div className="space-y-8">
        {sections.map(([section, sectionTables]) => (
          <section key={section}>
            <div className="mb-3 flex items-center gap-3">
              <h2 className="text-[11px] font-semibold uppercase tracking-[0.2em] text-ink-faint">
                {section}
              </h2>
              <div className="rule-accent flex-1 opacity-50" />
            </div>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(13rem,1fr))] gap-3">
              {sectionTables.map((table) => (
                <TableCard
                  key={table.id}
                  table={table}
                  now={now}
                  booking={bookingByTable.get(table.id) ?? null}
                  onSelect={onSelect}
                />
              ))}
            </div>
          </section>
        ))}
      </div>

      <OpenTableDialog
        table={pending}
        open={pending !== null}
        busy={openTable.isPending}
        onCancel={() => setPending(null)}
        onConfirm={(guestCount, notes, guest) =>
          pending && openTable.mutate({ tableId: pending.id, guestCount, notes, guest })
        }
      />
    </div>
  );
}
